import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import PublishingPanel from './PublishingPanel'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import type { OnHand, Price, Product, Revaluation, Variant, Warehouse } from '../api/types'
import { Perm } from '../api/types'
import { formatMoment, formatMoney, formatQty, toNumber } from '../ui/format'
import {
  ArchiveControl,
  BarcodeEditor,
  ProductEditor,
  ProductImage,
  VariantEditor,
} from './ProductEdit'
import {
  Banner,
  Button,
  Empty,
  ErrorBanner,
  Field,
  NumberInput,
  Panel,
  Row,
  Select,
  Spinner,
  TextInput,
} from '@shop/ui'

/**
 * الصنفُ ومتغيّراتُه — <b>ولا كميّةَ محفوظةٌ فيه</b>.
 *
 * الرصيدُ يُسأل عنه من دفتر الحركات (`/inventory/on-hand`) بصنفٍ ومستودعٍ معًا،
 * ولا يُقرأ من خانةٍ في المتغيّر: لا خانةَ أصلًا. وهذا ما يجعل الرصيدَ قابلًا
 * للتفسير — كلُّ رقمٍ فيه مجموعُ حركاتٍ لكلٍّ منها مصدرٌ ومستخدم.
 */

/** سعرٌ يُوضع أو يُصحَّح — و`PUT` لا `POST`: «اجعل السعرَ الحاليَّ كذا». */
function PriceEditor({ variant, onDone }: { variant: Variant; onDone: () => void }) {
  const [price, setPrice] = useState(variant.currentPrice === null ? '' : String(variant.currentPrice))
  const [reason, setReason] = useState('')
  const [error, setError] = useState<unknown>(null)

  const set = useMutation({
    mutationFn: () =>
      api<Price>(`/api/v1/variants/${variant.id}/price`, {
        method: 'PUT',
        body: { price: toNumber(price), reason: reason.trim() || null, currency: variant.currency ?? 'JOD' },
      }),
    onSuccess: () => {
      setError(null)
      onDone()
    },
    onError: setError,
  })

  const value = toNumber(price)

  return (
    <div className="mt-3 space-y-3 rounded-md border border-line bg-surface p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="السعر الجديد">
          <NumberInput value={price} onChange={setPrice} disabled={set.isPending} />
        </Field>
        <Field label="السبب" hint="يُقرأ يوم يُسأل «لماذا تغيّر السعر؟»">
          <TextInput value={reason} onChange={setReason} disabled={set.isPending} />
        </Field>
      </div>

      <ErrorBanner error={error} />

      <p className="text-xs text-ink-soft">
        يُغلق الصفُّ المفتوح ويُفتح غيرُه — ولا يُعدَّل سعرٌ في مكانه، فتاريخُ
        الأسعار يبقى كاملًا. وسعرُ البيع لا يمسّ كلفةَ المخزون القديمة.
      </p>

      <Button tone="primary" disabled={set.isPending || value === null} onClick={() => set.mutate()}>
        {set.isPending ? 'يُحفظ…' : 'اعتماد السعر'}
      </Button>
    </div>
  )
}

/** الرصيدُ في مستودعٍ يختاره الموظّف — والنقطةُ تطلب الاثنين معًا. */
function OnHandLookup({ variant, warehouses }: { variant: Variant; warehouses: Warehouse[] }) {
  const { can } = useAuth()
  const [warehouseId, setWarehouseId] = useState('')
  const [fixing, setFixing] = useState(false)

  // ⚠ وصلاحيّتان معًا: من يرى الكلفةَ ومن يُرحّل تسوية. وإخفاءُ الزرِّ راحةٌ
  //   لا حراسة — النقطةُ في الخادم تشترطهما على أيّ حال.
  const canFixCost = can(Perm.costView) && can(Perm.adjustmentPost)

  const onHand = useQuery({
    queryKey: ['on-hand', variant.id, warehouseId],
    queryFn: () =>
      api<OnHand>(`/api/v1/inventory/on-hand?variantId=${variant.id}&warehouseId=${warehouseId}`),
    enabled: warehouseId !== '',
  })

  return (
    <div className="mt-3 space-y-2">
      <Field label="الرصيد في مستودع">
        <Select value={warehouseId} onChange={setWarehouseId}>
          <option value="">اختر مستودعًا…</option>
          {warehouses.filter((w) => w.isActive).map((warehouse) => (
            <option key={warehouse.id} value={warehouse.id}>
              {warehouse.nameAr}
            </option>
          ))}
        </Select>
      </Field>

      {onHand.isPending && warehouseId !== '' && <Spinner label="يُحسب…" />}
      {onHand.isError && <ErrorBanner error={onHand.error} />}

      {onHand.data && (
        <div className="rounded-md border border-line bg-surface px-3 py-2">
          <Row label="الرصيد">
            <span className="tabular">{formatQty(onHand.data.qtyOnHand)}</span>
          </Row>

          <Row label="متوسّطُ الكلفة">
            {/*
              ⚠ **وصفرٌ ليس «رخيصًا» بل «لم تُسجَّل».** والفرقُ يقرّر الربحَ
                وأرضيّةَ السعر، فيُقال بالكلمة لا بالرقم وحدَه.
            */}
            {onHand.data.averageCostIn === null || onHand.data.averageCostIn === 0 ? (
              <span className="text-warn">بلا كلفة</span>
            ) : (
              formatMoney(onHand.data.averageCostIn)
            )}
          </Row>

          {canFixCost && onHand.data.qtyOnHand > 0 && (
            <div className="mt-2 border-t border-line pt-2">
              {fixing ? (
                <CostFixer
                  variantId={variant.id}
                  warehouseId={warehouseId}
                  onHand={onHand.data.qtyOnHand}
                  current={onHand.data.averageCostIn}
                  onClose={() => setFixing(false)}
                  onDone={() => {
                    setFixing(false)
                    void onHand.refetch()
                  }}
                />
              ) : (
                <Button onClick={() => setFixing(true)}>صحّح الكلفة</Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * تصحيحُ كلفةِ ما على الرفّ — <b>ويُقال إنّه مستندٌ لا محو</b>.
 *
 * <p>
 * ⚠ <b>ولا حقلَ كلفةٍ يُكتب فوقه.</b> الكلفةُ متوسّطٌ مُشتقٌّ من دفتر الحركات،
 * والدفترُ لا يُعدَّل. فالتصحيحُ يكتب ساقين — إخراجًا بالقديمة وإدخالًا
 * بالجديدة — ويبقيان في تاريخ الصنف. ومن يضغط يجب أن يعرف ذلك قبل أن يضغط،
 * لا أن يكتشفه في دفتر الصنف بعد شهر.
 * </p>
 */
function CostFixer({
  variantId,
  warehouseId,
  onHand,
  current,
  onClose,
  onDone,
}: {
  variantId: string
  warehouseId: string
  onHand: number
  current: number | null
  onClose: () => void
  onDone: () => void
}) {
  const [cost, setCost] = useState('')
  const [reason, setReason] = useState('')

  const fix = useMutation({
    mutationFn: () => api<Revaluation>('/api/v1/inventory/revalue', {
      method: 'POST',
      body: { variantId, warehouseId, unitCost: toNumber(cost), reason: reason.trim() },
    }),
    onSuccess: onDone,
  })

  const value = toNumber(cost)
  const ready = value !== null && value >= 0 && reason.trim().length >= 10 && !fix.isPending

  return (
    <div className="space-y-2">
      <Banner tone="info">
        التصحيحُ <b>يُكتب مستندًا</b> في دفتر الصنف — لا يمحو الكلفةَ القديمة.
        فتقاريرُ ما مضى تبقى كما كانت، ويُعرف متى تغيّرت ولماذا.
      </Banner>

      <Field label="الكلفةُ الجديدةُ للوحدة">
        <NumberInput value={cost} onChange={setCost} disabled={fix.isPending} />
      </Field>

      <Field label="السبب" hint="عشرةُ محارفَ فأكثر — يبقى في دفتر الصنف">
        <TextInput value={reason} onChange={setReason} disabled={fix.isPending} />
      </Field>

      {value !== null && value >= 0 && (
        <p className="text-xs text-ink-soft">
          يتغيّر تقييمُ <span className="tabular" dir="ltr">{formatQty(onHand)}</span> قطعةً
          {' '}من <b>{current === null || current === 0 ? 'بلا كلفة' : formatMoney(current)}</b>
          {' '}إلى <b>{formatMoney(value)}</b>
          {' — وفرقُ القيمة '}
          <b className="tabular" dir="ltr">
            {formatMoney(Math.round((value - (current ?? 0)) * onHand * 1000) / 1000)}
          </b>
        </p>
      )}

      <ErrorBanner error={fix.error} />

      <div className="flex flex-wrap gap-2">
        <Button tone="primary" disabled={!ready} onClick={() => fix.mutate()}>
          {fix.isPending ? 'يُكتب…' : 'اكتب مستندَ التصحيح'}
        </Button>

        <Button disabled={fix.isPending} onClick={onClose}>تراجَع</Button>
      </div>
    </div>
  )
}

function VariantCard({ variant, warehouses }: { variant: Variant; warehouses: Warehouse[] }) {
  const queries = useQueryClient()
  const { can } = useAuth()
  const [editing, setEditing] = useState(false)
  const [showHistory, setShowHistory] = useState(false)

  // لوحةٌ واحدةٌ مفتوحةٌ في كلّ مرّة: ثلاثُ لوحاتٍ مفتوحةٌ معًا تُطيل الصفَّ حتى
  // يضيع الزرُّ الذي يبحث عنه الموظّف.
  const [panel, setPanel] = useState<'details' | 'barcodes' | null>(null)

  const refresh = () => {
    void queries.invalidateQueries({ queryKey: ['product'] })
  }

  const toggle = (next: 'details' | 'barcodes') =>
    setPanel((current) => (current === next ? null : next))

  const prices = useQuery({
    queryKey: ['prices', variant.id],
    queryFn: () => api<Price[]>(`/api/v1/variants/${variant.id}/prices`),
    enabled: showHistory,
  })

  return (
    <li className="rounded-md border border-line p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {/* ⚠ `||` لا `??`: سلسلةُ `join` الفارغةُ ليست `null`، فـ`??` تمرّرها
            ويبقى العنوانُ فارغًا على الشاشة بلا أن يسقط شيء. */}
        <span className="text-sm font-medium">
          {variant.variantName
            || [variant.colour, variant.capacity].filter(Boolean).join(' · ')
            || 'المتغيّر الوحيد'}
        </span>
        <span className="tabular text-xs text-ink-soft" dir="ltr">
          {variant.sku}
        </span>
      </div>

      <Row label="السعر الحاليّ">{formatMoney(variant.currentPrice, variant.currency ?? 'JOD')}</Row>

      {variant.modelNumber && (
        <Row label="رقمُ الصانع"><span dir="ltr">{variant.modelNumber}</span></Row>
      )}

      {/* الباركوداتُ كلُّها لا الأوّلُ وحدَه: للمادّة الواحدة أرقامٌ عدّة —
          علبةُ المصنع وملصقُ المحلّ — والرئيسيُّ ما يُطبع. */}
      {variant.barcodes.length > 0 && (
        <Row label={variant.barcodes.length > 1 ? `الباركودات (${variant.barcodes.length})` : 'الباركود'}>
          <span className="tabular flex flex-wrap gap-2" dir="ltr">
            {variant.barcodes.map((b) => (
              <span key={b.id} className={b.isPrimary ? 'font-medium' : 'text-ink-soft'}>
                {b.barcode}
              </span>
            ))}
          </span>
        </Row>
      )}

      {(variant.requiresImei || variant.requiresSerial) && (
        <Row label="يُطلب عند البيع">
          {variant.requiresImei ? 'رقمُ IMEI' : 'رقمُ تسلسل'}
        </Row>
      )}

      {!variant.isActive && (
        <div className="mt-2">
          <Banner tone="warn">
            هذا المتغيّرُ مؤرشف{variant.archiveReason ? ` — ${variant.archiveReason}` : ''}.
          </Banner>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {can(Perm.priceEdit) && (
          <Button onClick={() => setEditing((current) => !current)}>
            {variant.currentPrice === null ? 'ضع سعرًا' : 'غيّر السعر'}
          </Button>
        )}
        <Button onClick={() => setShowHistory((current) => !current)}>تاريخُ الأسعار</Button>

        {can(Perm.productEdit) && (
          <>
            <Button onClick={() => toggle('details')}>تفاصيلُ المتغيّر</Button>
            <Button onClick={() => toggle('barcodes')}>الباركودات</Button>
            <ArchiveControl
              title="أرشِف المتغيّر"
              path={`/api/v1/variants/${variant.id}/archive`}
              isActive={variant.isActive}
              reasonHint="يُقرأ يوم يُسأل «لماذا اختفى هذا اللون؟»"
              onDone={refresh}
            />
          </>
        )}
      </div>

      {panel === 'details' && (
        <VariantEditor
          variant={variant}
          onDone={() => {
            setPanel(null)
            refresh()
          }}
        />
      )}

      {panel === 'barcodes' && <BarcodeEditor variant={variant} onChanged={refresh} />}

      {editing && (
        <PriceEditor
          variant={variant}
          onDone={() => {
            setEditing(false)
            void queries.invalidateQueries({ queryKey: ['product'] })
            void queries.invalidateQueries({ queryKey: ['prices', variant.id] })
          }}
        />
      )}

      {showHistory && (
        <div className="mt-3">
          {prices.isPending && <Spinner />}
          {prices.isError && <ErrorBanner error={prices.error} />}
          {prices.data?.length === 0 && <Empty>لا سعرَ بعد.</Empty>}

          <ul className="space-y-1 text-xs">
            {(prices.data ?? []).map((row) => (
              <li key={row.id} className="flex flex-wrap justify-between gap-2 border-b border-line py-1">
                <span className="tabular">{formatMoney(row.price, row.currency)}</span>
                <span className="tabular text-ink-soft">
                  {formatMoment(row.validFrom)} ← {row.validTo ? formatMoment(row.validTo) : 'الآن'}
                </span>
                {row.reason && <span className="text-ink-soft">{row.reason}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {can(Perm.movementView) && <OnHandLookup variant={variant} warehouses={warehouses} />}
    </li>
  )
}

export default function ProductDetail() {
  const { id = '' } = useParams()
  const { can } = useAuth()
  const [editing, setEditing] = useState(false)

  const product = useQuery({
    queryKey: ['product', id],
    queryFn: () => api<Product>(`/api/v1/products/${id}`),
  })

  const warehouses = useQuery({
    queryKey: ['warehouses', 'all'],
    queryFn: () => api<Warehouse[]>('/api/v1/warehouses'),
    enabled: can(Perm.warehouseView),
  })

  if (product.isPending) {
    return <Spinner />
  }

  if (product.isError) {
    return <ErrorBanner error={product.error} />
  }

  const p = product.data
  const refresh = () => {
    void product.refetch()
  }

  return (
    <>
      <Panel
        title={p.nameAr}
        actions={
          <span className="flex gap-2">
            {/* ⚠ النشرُ من صفحة الصنف نفسِها — يسحب الاسمَ والصورةَ والسعرَ ورابطَه. */}
            {can(Perm.socialPublish) && (
              <Link className="rounded-md border border-line px-3 py-1.5 text-sm" to={`/social/new?productId=${p.id}`}>
                نشر على مواقع التواصل
              </Link>
            )}
            {can(Perm.productEdit) && !editing && <Button onClick={() => setEditing(true)}>عدّل التفاصيل</Button>}
          </span>
        }
      >
        {editing ? (
          <ProductEditor
            product={p}
            onDone={() => {
              setEditing(false)
              refresh()
            }}
          />
        ) : (
          <div className="space-y-3">
            <ProductImage product={p} />

            <div>
              <Row label="الرمز">
                <span className="tabular" dir="ltr">{p.code}</span>
              </Row>
              {p.nameEn && <Row label="بالإنجليزيّة"><span dir="ltr">{p.nameEn}</span></Row>}
              {/* ⚠ **والقسمُ يُعرض ولو كان غائبًا** — وصفٌّ فارغٌ يُقرأ «لا
                  قسمَ لهذا الصنف»، وهو ما يجب أن يُرى ليُعالَج. */}
              <Row label="القسم">
                {p.categoryName ?? <span className="text-warn">بلا قسم</span>}
              </Row>

              {p.brandName && <Row label="الماركة">{p.brandName}</Row>}
              {p.model && <Row label="الطراز"><span dir="ltr">{p.model}</span></Row>}
              {p.warrantyMonths !== null && (
                <Row label="الكفالة">
                  {p.warrantyMonths} شهرًا{p.warrantyNote ? ` — ${p.warrantyNote}` : ''}
                </Row>
              )}
              {p.description && <Row label="الوصف">{p.description}</Row>}
            </div>

            {can(Perm.productEdit) && (
              <div className="flex flex-wrap gap-2">
                <ArchiveControl
                  title="أرشِف الصنف"
                  path={`/api/v1/products/${p.id}/archive`}
                  isActive={p.isActive}
                  reasonHint="تُؤرشَف متغيّراتُه الفعّالةُ معه باللحظة نفسِها."
                  onDone={refresh}
                />
              </div>
            )}

            {!p.isActive && (
              <Banner tone="warn">
                هذا الصنفُ مؤرشف{p.archiveReason ? ` — ${p.archiveReason}` : ''}.
              </Banner>
            )}
          </div>
        )}
      </Panel>

      <PublishingPanel product={p} onDone={refresh} />

      <Panel title="المتغيّرات">
        <ul className="space-y-3">
          {p.variants.map((variant) => (
            <VariantCard key={variant.id} variant={variant} warehouses={warehouses.data ?? []} />
          ))}
        </ul>
      </Panel>
    </>
  )
}
